import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "code", "name", "address", "city", "state", "country", "zipcode",
    "regionId", "affiliateId", "buildingTypeId",
    "rentalTrue", "rentalFalse",
    "codeError", "nameError", "addressError", "cityError", "countryError", "stateError",
    "stateMark", "serverError", "submitBtn"
  ]

  static values = { url: String }

  async submit() {
    this._clearErrors()
    if (!this._validate()) return

    this.submitBtnTarget.disabled = true

    const rental = this.rentalTrueTarget.checked
    const body = {
      tag_name: {
        code: this.codeTarget.value.trim(),
        name: this.nameTarget.value.trim(),
        address: this.addressTarget.value.trim(),
        city: this.cityTarget.value.trim(),
        state: this.stateTarget.value,
        country: this._selectedText(this.countryTarget),
        zipcode: this.zipcodeTarget.value.trim(),
        region_id: this.regionIdTarget.value || null,
        affiliate_id: this.affiliateIdTarget.value || null,
        building_type_id: this.buildingTypeIdTarget.value || null,
        rental_property: rental
      }
    }

    try {
      const token = document.querySelector("meta[name='csrf-token']")?.content
      const resp = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        body: JSON.stringify(body)
      })

      const data = await resp.json()

      if (resp.ok && data.resource) {
        this._resetForm()
        this.dispatch("created", { detail: data.resource })
        this.dispatch("close")
      } else {
        this._showServerErrors(data.errors || (data.error ? [data.error] : ["An error occurred"]))
      }
    } catch {
      this._showServerErrors(["An unexpected error occurred. Please try again."])
    } finally {
      this.submitBtnTarget.disabled = false
    }
  }

  // ── Country / State ───────────────────────────────────────────────

  async countryChanged() {
    this._markState()
    const countryCode = this.countryTarget.value
    const stateSelect = this.stateTarget

    // Clear existing options
    stateSelect.innerHTML = '<option value="">Select...</option>'

    if (!countryCode) return

    try {
      const response = await fetch(`/locations/subregions?country_code=${countryCode}`, {
        headers: { Accept: "application/json" }
      })
      const subregions = await response.json()
      subregions.forEach(sr => {
        const opt = document.createElement("option")
        opt.value = sr.name
        opt.textContent = sr.name
        stateSelect.appendChild(opt)
      })
    } catch { /* ignore */ }
  }

  // A state is part of a US address only: a property in the UK, Portugal or
  // Cape Verde has none to pick (Luma's sites, 2026-10-06).
  _stateNeeded() {
    return !this.countryTarget.value || this.countryTarget.value === "us"
  }

  _markState() {
    if (this.hasStateMarkTarget) this.stateMarkTarget.hidden = !this._stateNeeded()
  }

  // ── Validation ──────────────────────────────────────────────────────

  _validate() {
    let valid = true
    const required = [
      { target: this.codeTarget, error: this.codeErrorTarget },
      { target: this.nameTarget, error: this.nameErrorTarget },
      { target: this.addressTarget, error: this.addressErrorTarget },
      { target: this.cityTarget, error: this.cityErrorTarget },
      { target: this.countryTarget, error: this.countryErrorTarget }
    ]
    if (this._stateNeeded()) required.push({ target: this.stateTarget, error: this.stateErrorTarget })

    required.forEach(({ target, error }) => {
      if (!target.value.trim()) {
        target.classList.add("border-red-400", "ring-1", "ring-red-400")
        error.classList.remove("hidden")
        valid = false
      }
    })

    return valid
  }

  _clearErrors() {
    const fields = [this.codeTarget, this.nameTarget, this.addressTarget, this.cityTarget, this.countryTarget, this.stateTarget]
    fields.forEach(f => f.classList.remove("border-red-400", "ring-1", "ring-red-400"))

    const errors = [this.codeErrorTarget, this.nameErrorTarget, this.addressErrorTarget, this.cityErrorTarget, this.countryErrorTarget, this.stateErrorTarget]
    errors.forEach(e => e.classList.add("hidden"))

    this.serverErrorTarget.classList.add("hidden")
    this.serverErrorTarget.textContent = ""
  }

  _showServerErrors(errors) {
    this.serverErrorTarget.innerHTML = errors.map(e => `<div>${this._esc(e)}</div>`).join("")
    this.serverErrorTarget.classList.remove("hidden")
  }

  _resetForm() {
    const inputs = [this.codeTarget, this.nameTarget, this.addressTarget, this.cityTarget, this.zipcodeTarget]
    inputs.forEach(i => { i.value = "" })

    this.countryTarget.selectedIndex = 0
    this.stateTarget.innerHTML = '<option value="">Select...</option>'
    this.regionIdTarget.selectedIndex = 0
    this.affiliateIdTarget.selectedIndex = 0
    this.buildingTypeIdTarget.selectedIndex = 0
    this.rentalFalseTarget.checked = true
    this._markState()

    this._clearErrors()
  }

  _selectedText(select) {
    return select.selectedIndex >= 0 ? select.options[select.selectedIndex].text : ""
  }

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }
}
